import { Divider, keyframes, Stack, styled } from '@mui/material';

import AlertRedIcon from '@/assets/svg/ui-elements/alert-triangle-filled-red.svg';
import AlertIcon from '@/assets/svg/ui-elements/alert-triangle-outline-orange.svg';
import CircleCheckIcon from '@/assets/svg/ui-elements/circle-check.svg';
import XCloseIcon from '@/assets/svg/ui-elements/close.svg';
import ThumbUpGreen from '@/assets/svg/ui-elements/thumb-up-green.svg';
import { Typography } from '@/design-system';
import { colors } from '@/shared/design-system/colors';
import { ToastOptions } from '@/utils/emit-toast';

import { CustomButton } from './custom-button';

type CustomToastProps = {
  type?: 'error' | 'info' | 'success';
  visible: boolean;
  handleClose: () => void;
} & ToastOptions;

const DEFAULT_TOAST_SX = {
  borderRadius: '10px',
  padding: '16px 24px',
  border: '1px solid #323547',
  maxWidth: 400,
} as const;

const DEALS_TOAST_SX = {
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  maxWidth: 420,
  minHeight: 56,
} as const;

export const CustomToast = ({
  visible,
  handleClose,
  message,
  title,
  type,
  leftIcon,
  toastStyle = 'default',
}: CustomToastProps) => {
  const isDealsToast = toastStyle === 'deals';
  const StatusIcon =
    type === 'success' ? CircleCheckIcon : type === 'error' ? AlertRedIcon : AlertIcon;

  return (
    <AnimatedStack
      visible={visible}
      alignItems="center"
      gap={isDealsToast ? '10px' : '12px'}
      bgcolor="#1A1F32"
      sx={{
        ...(isDealsToast ? DEALS_TOAST_SX : DEFAULT_TOAST_SX),
      }}
    >
      {isDealsToast ? (
        <>
          <ThumbUpGreen />
          <Typography variant="body3" fontWeight={600}>
            {title}
          </Typography>
        </>
      ) : (
        <>
          {leftIcon ?? <StatusIcon style={{ flexShrink: 0 }} />}
          <Divider orientation="vertical" flexItem />
          <Stack flexDirection="column" gap="4px">
            <Typography fontWeight={600}>{title}</Typography>
            <Typography variant="body3" fontWeight={500} color={colors.text.secondary}>
              {message}
            </Typography>
          </Stack>
        </>
      )}
      <CustomButton
        noPadding
        onClick={handleClose}
        sx={{ alignSelf: isDealsToast ? 'center' : 'flex-start', marginLeft: 'auto' }}
      >
        <XCloseIcon width="16px" height="16px" />
      </CustomButton>
    </AnimatedStack>
  );
};

const slideIn = keyframes`
  from {
    transform: translateX(50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(50px);
    opacity: 0;
  }
`;

const AnimatedStack = styled(Stack, { shouldForwardProp: (prop) => prop !== 'visible' })`
  animation: ${({ visible }: { visible: boolean }) => (visible ? slideIn : slideOut)} 0.3s
    ease-in-out forwards;
`;
